"use client";

import { memo, useCallback, useMemo } from "react";
import { ChevronDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAtcAccordion } from "./atc-accordion-context";
import { getBadgeColor, getTextColor } from "../../atc-helpers";

const AtcAccordionItemComponent = ({
  item,
  depth = 0,
  onSelect,
  onLeafSelect,
}: {
  item: AtcItem;
  depth?: number;
  onSelect?: (item: AtcItem) => void;
  onLeafSelect?: (item: AtcItem) => void;
}) => {
  const { expandedIds, toggleExpanded } = useAtcAccordion();
  const hasChildren = item.children && item.children.length > 0;
  const isOpen = expandedIds.has(item.code);
  const isDepth0 = depth === 0;

  // Unified click handler
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only stopPropagation for non-depth0 items
      if (!isDepth0) {
        e.stopPropagation();
      }

      if (hasChildren) {
        // Handle expandable logic
        if (!e.ctrlKey && !e.metaKey) {
          toggleExpanded(item.code);
          if (e.shiftKey) {
            // For shift+click, expand/collapse all children
            const isCurrentlyOpen = expandedIds.has(item.code);
            const newExpandedState = !isCurrentlyOpen;
            const applyStateToTree = (node: AtcItem) => {
              if (node.children) {
                node.children.forEach((child) => {
                  toggleExpanded(child.code, newExpandedState);
                  applyStateToTree(child);
                });
              }
            };
            applyStateToTree(item);
          }
        }
        onSelect?.(item);
      } else {
        // Handle leaf logic
        onSelect?.(item);
        onLeafSelect?.(item);
      }
    },
    [
      item,
      hasChildren,
      isDepth0,
      expandedIds,
      toggleExpanded,
      onSelect,
      onLeafSelect,
    ],
  );

  // Memoized conditional styling
  const styles = useMemo(() => {
    const containerClass = !hasChildren
      ? "hover:text-remedium-blue flex cursor-pointer items-start gap-2.5 py-2.5 transition-colors select-none lg:py-1"
      : isDepth0
      ? "hover:text-remedium-blue flex w-full justify-between border-b-[1px] border-zinc-200 py-3 transition-all hover:cursor-pointer hover:[&_.to-scale]:scale-105 active:[&_.to-scale]:scale-95"
      : "hover:text-remedium-blue flex w-full cursor-pointer items-center justify-between border-b border-zinc-100 py-2.5 transition-all select-none lg:py-1.5";

    const codeClass = isDepth0
      ? cn(
          "to-scale flex h-[28px] max-w-[28px] min-w-[28px] items-center justify-center rounded-md text-sm font-medium text-white transition-all",
          getBadgeColor(depth),
        )
      : cn(
          "hover:text-remedium-blue min-w-[48px] text-sm font-bold",
          !hasChildren && "shrink-0",
          getTextColor(depth),
        );

    const titleClass = isDepth0
      ? "text-sm md:text-base font-medium"
      : !hasChildren
      ? "text-foreground hover:text-remedium-blue min-w-0 text-sm font-medium"
      : "hover:text-remedium-blue text-sm font-medium";

    const contentWrapperClass = isDepth0
      ? "flex items-center gap-4 select-none"
      : "flex items-start gap-2.5";

    const chevronSize = isDepth0 ? "20px" : "16px";
    const chevronClass = cn(
      "transition-all",
      isOpen && "rotate-180",
      !isDepth0 && "shrink-0",
    );

    const childrenContainerClass = isDepth0 ? "mx-7 my-3" : "mt-1 ml-8";
    const outerClass = isDepth0 ? "w-full" : undefined;

    return {
      containerClass,
      codeClass,
      titleClass,
      contentWrapperClass,
      chevronSize,
      chevronClass,
      childrenContainerClass,
      outerClass,
    };
  }, [hasChildren, isDepth0, isOpen, depth]);

  return (
    <div className={styles.outerClass}>
      <div className={styles.containerClass} onClick={handleClick}>
        <div className={styles.contentWrapperClass}>
          <div className={styles.codeClass}>{item.code}</div>
          <div className={styles.titleClass}>{item.titleText}</div>
        </div>
        {hasChildren && (
          <ChevronDownIcon
            size={styles.chevronSize}
            className={styles.chevronClass}
          />
        )}
      </div>
      {hasChildren && isOpen && (
        <div className={styles.childrenContainerClass}>
          <div className={"flex flex-col gap-1"}>
            {(item.children ?? []).map((child) => (
              <AtcAccordionItem
                key={child.code}
                item={child}
                depth={isDepth0 ? 1 : depth + 1}
                onSelect={onSelect}
                onLeafSelect={onLeafSelect}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const AtcAccordionItem = memo(AtcAccordionItemComponent);
